import type {
  EIP6963AnnounceProviderEvent,
  EIP6963RequestProviderEvent,
  MetaMaskInpageProvider,
  RequestArguments,
} from "@metamask/providers";
import { XrplConnector, XrplNetwork, XrplTransaction } from "../../types/xrpl";

enum EIP6963EventNames {
  Announce = "eip6963:announceProvider",
  Request = "eip6963:requestProvider",
}

enum NetworkChainId {
  MAINNET = 0,
  TESTNET = 1,
  DEVNET = 2,
}

declare global {
  interface WindowEventMap {
    [EIP6963EventNames.Announce]: EIP6963AnnounceProviderEvent;
    [EIP6963EventNames.Request]: EIP6963RequestProviderEvent;
  }
}

const snapId = "npm:xrpl-snap";

interface Network {
  chainId: NetworkChainId;
  name: string;
  nodeUrl: string;
  explorerUrl?: string;
}

interface SubmitTxResponse {
  engine_result: string;
  tx_json: {
    hash: string;
  };
}

export class XrplMetamaskSnapConnector implements XrplConnector {
  private provider?: MetaMaskInpageProvider | null;

  public async connect() {
    await this.initialize();

    await this.requestSnap(snapId);

    const { account } = await this.getAccount();

    return account;
  }

  public async signAndSubmit({
    network,
    tx,
  }: {
    network: XrplNetwork;
    tx: XrplTransaction;
  }) {
    if (!this.provider) throw new Error("Provider not initialized");

    const activeNetwork = await this.getActiveNetwork();
    const targetChainId = this.getNetworkChainId(network);

    if (activeNetwork.chainId !== targetChainId) {
      await this.changeNetwork(targetChainId);
    }

    const res = await this.invokeSnap<{
      result: SubmitTxResponse;
    }>({
      method: "xrpl_signAndSubmit",
      params: tx,
    });

    if (!res?.result) {
      throw new Error("Invalid transaction result");
    }

    const txResponse = res.result.tx_json;
    const txStatus = res.result.engine_result;

    return {
      hash: txResponse.hash,
      status: txStatus,
    };
  }

  // Cannot use constructor because of the async initialization
  private async initialize() {
    this.provider = await this.getProvider();
  }

  private async requestSnap(id: string): Promise<void> {
    await this.request({
      method: "wallet_requestSnaps",
      params: {
        [id]: {},
      },
    });
  }

  private async getAccount(): Promise<{
    account: string;
    publicKey: string;
  }> {
    return this.invokeSnap({
      method: "xrpl_getAccount",
      params: undefined,
    });
  }

  private async getActiveNetwork(): Promise<Network> {
    return this.invokeSnap({
      method: "xrpl_getActiveNetwork",
      params: undefined,
    });
  }

  private async changeNetwork(
    chainId: NetworkChainId
  ): Promise<Network | null> {
    try {
      return await this.invokeSnap({
        method: "xrpl_changeNetwork",
        params: { chainId },
      });
    } catch (error) {
      // changeNetwork may fail if the user is already on the correct network
      // so we don't need to handle the error here
      return null;
    }
  }

  private async invokeSnap<T>({
    params,
    method,
  }: {
    method: string;
    params: any;
  }): Promise<T> {
    return this.request({
      method: "wallet_invokeSnap",
      params: {
        snapId,
        request: {
          method,
          params,
        },
      },
    }) as Promise<T>;
  }

  private async getProvider() {
    if (typeof window === "undefined") {
      return null;
    }

    if (await this.hasSnapsSupport(window.ethereum)) {
      return window.ethereum;
    }

    if (window.ethereum?.detected) {
      for (const provider of window.ethereum.detected) {
        if (await this.hasSnapsSupport(provider)) {
          return provider;
        }
      }
    }

    if (window.ethereum?.providers) {
      for (const provider of window.ethereum.providers) {
        if (await this.hasSnapsSupport(provider)) {
          return provider;
        }
      }
    }

    // Need to use EIP-6963 provider discovery because we need Metamask to be installed
    // other wallets overriding Metamask won't work here because they don't support Metamask snaps
    const eip6963Provider = await this.getMetamaskEIP6963Provider();

    if (eip6963Provider && (await this.hasSnapsSupport(eip6963Provider))) {
      return eip6963Provider;
    }

    return null;
  }

  private async hasSnapsSupport(
    provider: MetaMaskInpageProvider = window.ethereum
  ): Promise<boolean> {
    try {
      await provider.request({
        method: "wallet_getSnaps",
      });
      return true;
    } catch {
      return false;
    }
  }

  private async getMetamaskEIP6963Provider() {
    return new Promise<MetaMaskInpageProvider | null>((rawResolve) => {
      // Timeout looking for providers after 500ms
      const timeout = setTimeout(() => {
        resolve(null);
      }, 500);

      function resolve(provider: MetaMaskInpageProvider | null) {
        // Resolve the promise with a Metamask provider and clean up the timeout.
        window.removeEventListener(
          EIP6963EventNames.Announce,
          onAnnounceProvider
        );
        clearTimeout(timeout);
        rawResolve(provider);
      }

      /**
       * Listener for the EIP6963 announceProvider event.
       * Resolves the promise if a Metamask provider is found.
       */
      function onAnnounceProvider({ detail }: EIP6963AnnounceProviderEvent) {
        const { info, provider } = detail;

        if (info.rdns.includes("io.metamask")) {
          resolve(provider);
        }
      }

      window.addEventListener(EIP6963EventNames.Announce, onAnnounceProvider);

      window.dispatchEvent(new Event(EIP6963EventNames.Request));
    });
  }

  private async request({ method, params }: RequestArguments) {
    if (!this.provider) {
      throw new Error("XRPL Metamask provider not initialized");
    }
    return this.provider.request({ method, params });
  }

  private getNetworkChainId(network: XrplNetwork): NetworkChainId {
    switch (network) {
      case XrplNetwork.MAINNET:
        return NetworkChainId.MAINNET;
      case XrplNetwork.TESTNET:
        return NetworkChainId.TESTNET;
      default:
        throw new Error(`Network ${network} is not supported`);
    }
  }
}
